import { useState, useEffect } from "react";
import { 
  GripVertical,
  BarChart3, 
  Calendar, 
  Users, 
  AlertTriangle, 
  CheckCircle, 
  Brain,
  Camera,
  FileText,
  List,
  PieChart,
  Truck,
  Paperclip,
  CheckSquare,
  X
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export interface WorkDashboardItem {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  visible: boolean;
  order: number;
  minimized?: boolean;
  subItems?: (Omit<WorkDashboardItem, 'visible' | 'order' | 'subItems'> & { visible?: boolean })[];
  parent?: string;
}

export interface WorkDashboardLayout {
  obraId: string;
  items: WorkDashboardItem[];
}

interface WorkDashboardItemsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  obraId: string;
  items: WorkDashboardItem[];
  onApply: (items: WorkDashboardItem[]) => void;
}

const defaultItems: Omit<WorkDashboardItem, 'visible' | 'order'>[] = [
  {
    id: "statsCards",
    title: "Cartões de Estatísticas",
    description: "Resumo de métricas importantes",
    icon: BarChart3,
    subItems: [
      {
        id: "statsCards.reports",
        title: "Relatórios Preenchidos",
        description: "Relatórios de progresso da obra",
        icon: FileText,
        parent: "statsCards"
      },
      {
        id: "statsCards.occurrences",
        title: "Ocorrências",
        description: "Ocorrências e problemas registrados",
        icon: AlertTriangle,
        parent: "statsCards"
      },
      {
        id: "statsCards.workforce",
        title: "Mão de Obra",
        description: "Quantidade de trabalhadores",
        icon: Users,
        parent: "statsCards"
      },
      {
        id: "statsCards.equipment",
        title: "Equipamentos",
        description: "Equipamentos em uso na obra",
        icon: Truck,
        parent: "statsCards"
      },
      {
        id: "statsCards.photos",
        title: "Fotos",
        description: "Fotos da obra",
        icon: Camera,
        parent: "statsCards"
      },
      {
        id: "statsCards.attachments",
        title: "Anexos",
        description: "Documentos e anexos",
        icon: Paperclip,
        parent: "statsCards"
      },
      {
        id: "statsCards.approveRdos",
        title: "Aprovar RDOs",
        description: "RDOs pendentes de aprovação",
        icon: CheckSquare,
        parent: "statsCards"
      }
    ]
  },
  {
    id: "attendanceChart",
    title: "Histograma de Pessoas",
    description: "Gráfico de frequência de pessoas",
    icon: Users
  },
  {
    id: "workChart",
    title: "Condições Climáticas",
    description: "Análise de dias úteis vs impraticáveis",
    icon: Calendar
  },
  {
    id: "tasksAndSuggestions",
    title: "Tarefas e Sugestões",
    description: "Gestão de tarefas e recomendações da IA",
    icon: Brain
  },
  {
    id: "photoGallery",
    title: "Galeria de Fotos",
    description: "Fotos recentes da obra",
    icon: Camera
  },
  {
    id: "recentReports",
    title: "Relatórios Recentes",
    description: "Últimos relatórios de obra",
    icon: FileText
  },
  {
    id: "taskList",
    title: "Lista de Tarefas",
    description: "Tarefas pendentes e em andamento",
    icon: List
  }
];

export const WorkDashboardItemsSheet = ({
  open,
  onOpenChange,
  obraId,
  items,
  onApply
}: WorkDashboardItemsSheetProps) => {
  console.log('WorkDashboardItemsSheet rendered with items:', items);
  const [currentItems, setCurrentItems] = useState<WorkDashboardItem[]>(items);
  const [draggedItem, setDraggedItem] = useState<WorkDashboardItem | null>(null);

  useEffect(() => {
    console.log('useEffect triggered, setting currentItems to:', items);
    setCurrentItems(items);
  }, [items]);

  const handleToggle = (id: string, visible: boolean) => {
    const updatedItems = currentItems.map(item => {
      if (item.id === id) {
        return { ...item, visible };
      }
      // Update sub-items if they exist
      if (item.subItems) {
        const updatedSubItems = item.subItems.map(subItem =>
          subItem.id === id ? { ...subItem, visible } : subItem
        );
        return { ...item, subItems: updatedSubItems };
      }
      return item;
    });
    
    setCurrentItems(updatedItems);
    
    // Auto-apply changes immediately
    const layout: WorkDashboardLayout = {
      obraId,
      items: updatedItems
    };
    localStorage.setItem(`dashboardLayout:${obraId}`, JSON.stringify(layout));
    onApply(updatedItems);
  };

  const handleDragStart = (e: React.DragEvent, item: WorkDashboardItem) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetItem: WorkDashboardItem) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.id === targetItem.id) {
      setDraggedItem(null);
      return;
    }

    setCurrentItems(prev => {
      const newItems = [...prev];
      const draggedIndex = newItems.findIndex(item => item.id === draggedItem.id);
      const targetIndex = newItems.findIndex(item => item.id === targetItem.id);
      
      // Remove dragged item
      const [removed] = newItems.splice(draggedIndex, 1);
      
      // Insert at target position
      newItems.splice(targetIndex, 0, removed);
      
      // Update order values
      return newItems.map((item, index) => ({ ...item, order: index }));
    });
    
    setDraggedItem(null);
  };

  const handleApply = () => {
    // Save to localStorage with obra-specific key
    const layout: WorkDashboardLayout = {
      obraId,
      items: currentItems
    };
    localStorage.setItem(`dashboardLayout:${obraId}`, JSON.stringify(layout));
    
    onApply(currentItems);
    onOpenChange(false);
  };

  const handleCancel = () => {
    setCurrentItems(items);
    onOpenChange(false);
  };

  const handleResetDefaults = () => {
    const resetItems: WorkDashboardItem[] = defaultItems.map((item, index) => {
      const baseItem = {
        ...item,
        visible: true,
        order: index
      };
      
      if (item.subItems) {
        return {
          ...baseItem,
          subItems: item.subItems.map((subItem, subIndex) => ({
            ...subItem,
            visible: true,
            order: subIndex
          }))
        };
      }
      
      return baseItem;
    });
    setCurrentItems(resetItems);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => onOpenChange(false)}
      />

      {/* Container do modal */}
      <div className="relative z-10 w-[92vw] max-w-2xl rounded-2xl bg-background shadow-xl border flex flex-col max-h-[90dvh]">
        {/* Header */}
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h3 className="text-base font-semibold">Gerenciar Itens</h3>
          <button
            aria-label="Fechar"
            onClick={() => onOpenChange(false)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring sm:h-9 sm:w-9"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* CONTEÚDO ROLÁVEL */}
        <div
          className="px-5 py-4 overflow-y-auto"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <p className="text-sm text-muted-foreground mb-4">
            Configure a visibilidade e ordem dos componentes do dashboard
          </p>
          
          <div className="space-y-3">
            {currentItems.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.id}>
                  <div
                    className="flex items-center space-x-3 p-3 rounded-lg border bg-card cursor-move"
                    draggable
                    onDragStart={(e) => handleDragStart(e, item)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, item)}
                  >
                    <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    {Icon && <Icon className="h-5 w-5 text-primary flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <Label 
                          htmlFor={item.id}
                          className="text-sm font-medium cursor-pointer truncate"
                        >
                          {item.title}
                        </Label>
                        <Switch
                          id={item.id}
                          checked={item.visible}
                          onCheckedChange={(checked) => handleToggle(item.id, checked)}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {item.description}
                      </p>
                    </div>
                  </div>
                  
                  {/* Sub-items */}
                  {item.subItems && (
                    <div className="ml-8 mt-2 space-y-2">
                      {item.subItems.map((subItem) => {
                        const SubIcon = subItem.icon;
                        return (
                          <div
                            key={subItem.id}
                            className="flex items-center space-x-3 p-2 rounded-md border bg-muted/30"
                           >
                             {SubIcon && <SubIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <Label 
                                  htmlFor={subItem.id}
                                  className="text-xs font-medium cursor-pointer truncate"
                                >
                                  {subItem.title}
                                </Label>
                                <Switch
                                  id={subItem.id}
                                  checked={subItem.visible !== false}
                                  onCheckedChange={(checked) => handleToggle(subItem.id, checked)}
                                />
                              </div>
                              <p className="text-xs text-muted-foreground/70 mt-0.5">
                                {subItem.description}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
};

// Utility function to load dashboard layout for a specific obra
export const loadWorkDashboardLayout = (obraId: string): WorkDashboardItem[] => {
  console.log('loadWorkDashboardLayout called for obraId:', obraId);
  
  // Create a map of icons from defaultItems for restoration
  const iconMap = new Map();
  defaultItems.forEach(item => {
    iconMap.set(item.id, item.icon);
    if (item.subItems) {
      item.subItems.forEach(subItem => {
        iconMap.set(subItem.id, subItem.icon);
      });
    }
  });
  
  try {
    const saved = localStorage.getItem(`dashboardLayout:${obraId}`);
    if (saved) {
      const layout: WorkDashboardLayout = JSON.parse(saved);
      console.log('Loaded saved layout:', layout);
      
      // Check if saved layout has all default items, if not, clear it and use default
      const savedItemIds = layout.items.map(item => item.id);
      const defaultItemIds = defaultItems.map(item => item.id);
      const missingItems = defaultItemIds.filter(id => !savedItemIds.includes(id));
      
      if (missingItems.length > 0) {
        console.log('Found missing items in saved layout:', missingItems);
        console.log('Clearing old layout and using default layout');
        localStorage.removeItem(`dashboardLayout:${obraId}`);
        // Fall through to default layout
      } else {
        // Filter out workHeader and restore icons and ensure proper structure
        return layout.items
          .filter(item => item.id !== 'workHeader') // Remove workHeader if it exists
          .map(item => {
            const restoredItem = {
              ...item,
              icon: iconMap.get(item.id) || FileText // Fallback icon
            };
            
            // Force update title for workChart to ensure it shows "Condições Climáticas"
            if (item.id === 'workChart') {
              restoredItem.title = "Condições Climáticas";
              restoredItem.description = "Análise de dias úteis vs impraticáveis";
            }
            
            if (item.id === 'statsCards') {
              // Ensure statsCards has proper subItems with icons
              const defaultStatsCard = defaultItems.find(d => d.id === 'statsCards');
              restoredItem.subItems = defaultStatsCard?.subItems?.map((subItem, subIndex) => ({
                ...subItem,
                visible: item.subItems?.find(s => s.id === subItem.id)?.visible !== false,
                order: subIndex,
                icon: iconMap.get(subItem.id) || FileText // Restore icon
              })) || [];
            } else if (item.subItems) {
              // Restore icons for other items with subItems
              restoredItem.subItems = item.subItems.map(subItem => ({
                ...subItem,
                icon: iconMap.get(subItem.id) || FileText
              }));
            }
            
            return restoredItem;
          });
      }
    }
  } catch (error) {
    console.error('Error loading dashboard layout:', error);
  }

  // Return default layout with proper structure (excluding workHeader)
  const defaultLayout = defaultItems.map((item, index) => {
    const baseItem = {
      ...item,
      visible: true,
      order: index
    };
    
    if (item.subItems) {
      console.log('Processing item with subItems:', item.id, item.subItems);
      return {
        ...baseItem,
        subItems: item.subItems.map((subItem, subIndex) => ({
          ...subItem,
          visible: true,
          order: subIndex
        }))
      };
    }
    
    return baseItem;
  });
  
  console.log('Returning default layout:', defaultLayout);
  return defaultLayout;
};